import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ChevronDown, ChevronRight, Plus, Video, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { http, registrarDonos } from "@/lib/api";

// Lote 168 — aba "Agenda" da ficha do processo: o que está marcado neste processo (prazos, audiências, reuniões…),
// o que vem primeiro no alto; o que já passou, foi baixado ou cancelado fica em "Anteriores", recolhido.
const SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const dia = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return { dm: "—", sem: "" };
  return { dm: `${m[3]}/${m[2]}/${m[1].slice(2)}`, sem: SEMANA[new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay()] };
};
const SIT = {
  conferir: { txt: "Conferir", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  cancelado: { txt: "Cancelado", cls: "bg-stone-100 text-stone-500 border-stone-200" },
  feito: { txt: "Feito", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};
const TIPO = { audiencia: "Audiência", prazo: "Prazo", reuniao: "Reunião" };

function Linha({ it, onAbrir, apagada }) {
  const d = dia(it.date); const sit = SIT[it.situacao];
  return (
    <li>
      <button type="button" data-testid={`agenda-item-${it.pub_id}`} onClick={() => onAbrir(it)}
        className={`w-full text-left grid grid-cols-[4.75rem_1fr_auto] items-center gap-x-3 gap-y-0.5 px-3 py-3 min-h-[56px] hover:bg-stone-50 ${apagada ? "opacity-70" : ""}`}>
        <span className="tabular-nums">
          <span className="block text-sm font-semibold text-stone-900">{d.dm}</span>
          <span className="block text-[11px] uppercase tracking-wide text-stone-400">{d.sem}{it.time ? ` · ${String(it.time).slice(0, 5)}` : ""}</span>
        </span>
        <span className="min-w-0">
          <span className={`block text-sm font-medium text-stone-900 break-words ${it.situacao === "cancelado" ? "line-through" : ""}`}>
            {it.titulo}{TIPO[it.event_type] && it.titulo !== TIPO[it.event_type] ? <span className="ml-1.5 text-[11px] font-normal text-stone-400">{TIPO[it.event_type]}</span> : null}
          </span>
          {(it.local || it.virtual) && (
            <span className="flex items-center gap-1 text-xs text-stone-500 truncate">
              {it.virtual && <Video className="w-3 h-3 shrink-0" strokeWidth={1.75} />}{it.local || "Videoconferência"}
            </span>
          )}
        </span>
        {sit ? <span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 ${sit.cls}`}>{sit.txt}</span> : <ChevronRight className="w-4 h-4 text-stone-300" />}
      </button>
    </li>
  );
}

export default function AgendaProcesso({ processoId, deDono, meuId, reloadKey = 0, onNovo }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(false);
  const [antes, setAntes] = useState(false);
  const [aberta, setAberta] = useState(false);
  const load = useCallback(async () => {
    setErro(false);
    try {
      const bruto = (await http.get(`/processos/${processoId}/agenda`)).data || {};
      const r = { proximos: bruto.proximos || [], anteriores: bruto.anteriores || [] };
      if (deDono) registrarDonos([...r.proximos, ...r.anteriores].map((x) => ({ pub_id: x.pub_id, dono_id: deDono })), meuId, "pub_id");
      setD(r);
    } catch { setErro(true); }
  }, [processoId, deDono, meuId]);
  useEffect(() => { load(); }, [load, reloadKey]);
  const abrir = (it) => navigate(`/audiencia/${it.pub_id}`);

  const n = d ? d.proximos.length : null;
  const resumo = erro ? "não carregou" : n === null ? "…" : n === 0 ? "nenhum por vir" : `${n} por vir`;
  return (
    <section className="bg-white border border-stone-200 rounded-md" data-testid="agenda-processo">
      <button type="button" data-testid="agenda-toggle" aria-expanded={aberta} onClick={() => setAberta((v) => !v)}
        className="w-full flex items-center gap-2 min-h-[52px] px-4 md:px-5 text-left hover:bg-stone-50 rounded-md">
        <CalendarDays className="w-4 h-4 text-stone-500" strokeWidth={1.75} />
        <span className="flex-1 min-w-0 flex items-baseline gap-2">
          <h2 className="font-display text-base font-bold text-stone-900">Agenda do processo</h2>
          <span className="text-xs text-stone-500" data-testid="agenda-resumo">· {resumo}</span>
        </span>
        {aberta ? <ChevronDown className="w-4 h-4 text-stone-500" /> : <ChevronRight className="w-4 h-4 text-stone-500" />}
      </button>
      {aberta && (
      <div className="border-t border-stone-100" data-testid="agenda-corpo">
      <div className="flex justify-end px-4 py-2">
        <Button type="button" size="sm" data-testid="agenda-novo-evento" onClick={onNovo} className="h-8 gap-1.5 text-xs font-semibold bg-gold-500 text-black border border-gold-600 hover:bg-gold-400">
          <Plus className="w-3.5 h-3.5" strokeWidth={2.25} /> Novo evento
        </Button>
      </div>
      {erro && (
        <div className="p-4 text-sm text-rose-700 flex items-center gap-3" data-testid="agenda-erro">
          Não consegui carregar a agenda deste processo.
          <Button type="button" size="sm" variant="outline" data-testid="agenda-tentar" onClick={load} className="gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Tentar de novo</Button>
        </div>
      )}
      {!erro && !d && <p className="p-4 text-sm text-stone-500" data-testid="agenda-carregando">Carregando…</p>}
      {d && (
        <>
          {d.proximos.length > 0 ? (
            <ul className="divide-y divide-stone-100" data-testid="agenda-proximos">{d.proximos.map((it) => <Linha key={it.pub_id} it={it} onAbrir={abrir} />)}</ul>
          ) : (
            <p className="p-4 text-sm text-stone-500" data-testid="agenda-vazia">Nenhum compromisso por vir neste processo. Use “Novo evento” para agendar um prazo ou uma audiência.</p>
          )}
          {d.anteriores.length > 0 && (
            <div className="border-t border-stone-100">
              <button type="button" data-testid="agenda-anteriores-toggle" aria-expanded={antes} onClick={() => setAntes((v) => !v)}
                className="w-full flex items-center gap-2 px-4 min-h-[44px] text-sm font-semibold text-stone-600 hover:bg-stone-50">
                {antes ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} Anteriores ({d.anteriores.length})
              </button>
              {antes && <ul className="divide-y divide-stone-100 border-t border-stone-100" data-testid="agenda-anteriores">{d.anteriores.map((it) => <Linha key={it.pub_id} it={it} onAbrir={abrir} apagada />)}</ul>}
            </div>
          )}
        </>
      )}
      </div>
      )}
    </section>
  );
}
